import { useEffect } from 'react';

import { useIsSystemAdmin, useProfile } from '../../modules/auth/hooks';

/**
 * AminosChat — third-party chat widget loader.
 *
 * Injects the Aminos.ai chat plugin script into <head> on mount and tears it
 * (plus any DOM nodes the widget added) back down if the viewer turns out to
 * be a system admin. Gated by:
 *
 *   - `import.meta.env.PROD` so the widget only loads in production builds.
 *   - The profile query having resolved. Without this gate, the effect runs
 *     while `useIsSystemAdmin()` still reports false (profile loading),
 *     injects the script, and the widget's own DOM nodes are then stuck
 *     mounted even after admin status flips true.
 *   - The viewer not being a system admin. Admin-side work (admin/events,
 *     billing exports, audit trawls) is the wrong audience for a customer
 *     support chat bubble. Impersonation correctly flips this off: a
 *     system admin impersonating a customer becomes a non-admin in the
 *     eyes of `useIsSystemAdmin`, so they see the same chat the customer
 *     would.
 *
 * - `npm run dev`        → DEV mode  → widget skipped
 * - `npm run build:dev`  → DEV mode  → widget skipped (dev SWA stays clean)
 * - `npm run build:prod` → PROD mode → widget loads for non-admins
 */
const AMINOS_SCRIPT_SRC = 'https://app.aminos.ai/js/chat_plugin.js';
const AMINOS_BOT_ID = '9019';
const AMINOS_SCRIPT_ID = 'aminos-chat-plugin';

function removeAminosWidget() {
  document.getElementById(AMINOS_SCRIPT_ID)?.remove();
  document
    .querySelectorAll<HTMLElement>(
      '[id^="aminos"], [class*="aminos"], iframe[src*="aminos.ai"], script[src*="aminos.ai"]',
    )
    .forEach((node) => node.remove());
}

export default function AminosChat() {
  const isSystemAdmin = useIsSystemAdmin();
  const { isFetched } = useProfile();

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (!isFetched) return;
    if (isSystemAdmin) {
      removeAminosWidget();
      return;
    }
    if (document.getElementById(AMINOS_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = AMINOS_SCRIPT_ID;
    script.src = AMINOS_SCRIPT_SRC;
    script.async = true;
    script.dataset.botId = AMINOS_BOT_ID;
    document.head.appendChild(script);
  }, [isFetched, isSystemAdmin]);

  return null;
}
